import type { RuntimeCaching } from 'serwist';
import {
  CacheFirst,
  ExpirationPlugin,
  NetworkFirst,
  NetworkOnly,
  StaleWhileRevalidate,
} from 'serwist';

const PAGES_CACHE_NAME = {
  rscPrefetch: 'pages-rsc-prefetch',
  rsc: 'pages-rsc',
  html: 'pages',
} as const;

export const runtimeCaching: RuntimeCaching[] =
  process.env.NODE_ENV !== 'production'
    ? [
        {
          matcher: /.*/i,
          handler: new NetworkOnly(),
        },
      ]
    : [
        // --- STATIC ASSETS ---

        // Specific external configs (short cache time to stay updated)
        {
          matcher: /https:\/\/.*ngp\.zdf\.de\/.*\.(json)/,
          handler: new CacheFirst({
            cacheName: 'session-configs',
            plugins: [
              new ExpirationPlugin({
                maxEntries: 10,
                maxAgeSeconds: 30 * 60, // 30 minutes
                maxAgeFrom: 'last-used',
              }),
            ],
          }),
        },
        // External player assets (long cache with background updates)
        {
          matcher: /https:\/\/.*ngp\.zdf\.de\/.*\.(js|css|woff)/,
          handler: new StaleWhileRevalidate({
            cacheName: 'player-assets',
            plugins: [
              new ExpirationPlugin({
                maxEntries: 10,
                maxAgeSeconds: 30 * 24 * 60 * 60, // 30 days
              }),
            ],
          }),
        },

        // Fonts
        {
          matcher: /\.(?:eot|otf|ttc|ttf|woff|woff2|font.css)$/i,
          handler: new StaleWhileRevalidate({
            cacheName: 'static-font-assets',
            plugins: [
              new ExpirationPlugin({
                maxEntries: 8,
                maxAgeSeconds: 7 * 24 * 60 * 60, // 7 days
                maxAgeFrom: 'last-used',
              }),
            ],
          }),
        },

        // Images
        {
          matcher: ({ request }) =>
            request.headers.get('sec-fetch-dest') === 'image' ||
            /\.(?:png|jpg|jpeg|webp|gif|svg)$/i.test(request.url) ||
            /^https:\/\/www\.zdf\.de\/assets(?:\/|$)/.test(request.url),
          handler: new StaleWhileRevalidate({
            cacheName: 'image-assets',
            plugins: [
              new ExpirationPlugin({
                maxEntries: 128,
                maxAgeSeconds: 30 * 24 * 60 * 60, // 30 days
                maxAgeFrom: 'last-used',
              }),
            ],
          }),
        },

        // Static Next.js build JS (immutable, can be CacheFirst)
        {
          matcher: /\/_next\/static.+\.js$/i,
          handler: new CacheFirst({
            cacheName: 'next-static-js-assets',
            plugins: [
              new ExpirationPlugin({
                maxEntries: 64,
                maxAgeSeconds: 30 * 24 * 60 * 60, // 30 days
                maxAgeFrom: 'last-used',
              }),
            ],
          }),
        },

        // Other JS (dynamic bundles or other assets)
        {
          matcher: /\.(?:js)$/i,
          handler: new StaleWhileRevalidate({
            cacheName: 'static-js-assets',
            plugins: [
              new ExpirationPlugin({
                maxEntries: 48,
                maxAgeSeconds: 24 * 60 * 60, // 24 hours
                maxAgeFrom: 'last-used',
              }),
            ],
          }),
        },

        // CSS and Less styles
        {
          matcher: /\.(?:css|less)$/i,
          handler: new StaleWhileRevalidate({
            cacheName: 'static-style-assets',
            plugins: [
              new ExpirationPlugin({
                maxEntries: 32,
                maxAgeSeconds: 24 * 60 * 60, // 24 hours
                maxAgeFrom: 'last-used',
              }),
            ],
          }),
        },

        // --- DYNAMIC DATA ---

        // Next.js data fetching (_next/data)
        {
          matcher: /\/_next\/data\/.+\/.+\.json$/i,
          handler: new NetworkFirst({
            cacheName: 'next-data',
            plugins: [
              new ExpirationPlugin({
                maxEntries: 32,
                maxAgeSeconds: 24 * 60 * 60, // 24 hours
                maxAgeFrom: 'last-used',
              }),
            ],
          }),
        },

        // Other static data assets
        {
          matcher: /\.(?:json|xml|csv)$/i,
          handler: new NetworkFirst({
            cacheName: 'static-data-assets',
            plugins: [
              new ExpirationPlugin({
                maxEntries: 64,
                maxAgeSeconds: 24 * 60 * 60, // 24 hours
                maxAgeFrom: 'last-used',
              }),
            ],
          }),
        },

        // --- PAGES ---

        // Prefetched RSC Pages
        {
          matcher: ({ request, url: { pathname }, sameOrigin }) =>
            request.headers.get('RSC') === '1' &&
            request.headers.get('Next-Router-Prefetch') === '1' &&
            sameOrigin &&
            !pathname.startsWith('/api/'),
          handler: new NetworkFirst({
            cacheName: PAGES_CACHE_NAME.rscPrefetch,
            plugins: [
              new ExpirationPlugin({
                maxEntries: 32,
                maxAgeSeconds: 24 * 60 * 60,
              }),
            ],
          }),
        },

        // Normal RSC Pages
        {
          matcher: ({ request, url: { pathname }, sameOrigin }) =>
            request.headers.get('RSC') === '1' &&
            sameOrigin &&
            !pathname.startsWith('/api/'),
          handler: new NetworkFirst({
            cacheName: PAGES_CACHE_NAME.rsc,
            plugins: [
              new ExpirationPlugin({
                maxEntries: 32,
                maxAgeSeconds: 24 * 60 * 60,
              }),
            ],
          }),
        },

        // HTML Pages
        {
          matcher: ({ request, url: { pathname }, sameOrigin }) =>
            request.headers.get('Content-Type')?.includes('text/html') &&
            sameOrigin &&
            !pathname.startsWith('/api/'),
          handler: new NetworkFirst({
            cacheName: PAGES_CACHE_NAME.html,
            plugins: [
              new ExpirationPlugin({
                maxEntries: 32,
                maxAgeSeconds: 24 * 60 * 60,
              }),
            ],
          }),
        },

        // Other same-origin requests (catch-all)
        {
          matcher: ({ url: { pathname }, sameOrigin }) =>
            sameOrigin && !pathname.startsWith('/api/'),
          handler: new NetworkFirst({
            cacheName: 'others',
            plugins: [
              new ExpirationPlugin({
                maxEntries: 32,
                maxAgeSeconds: 24 * 60 * 60,
              }),
            ],
          }),
        },
      ];
